import { forwardRef, useEffect, useMemo, useState } from "react";
import { FV_LOOPS_VALIDATION_ENDPOINT } from "../../api/endpoints";
import { submitDynamicLoops } from "../../api/forms/fv-loops";
import { DynamicLoops } from "../../api/forms/fv-loops/types";
import { Nullable } from "../../api/types";
import useDataInputForm, { FORM_IDS } from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import { FormHandle, InputTypes } from "../../types";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";
import { fetcher } from "../../api/utils";
import { toSnakeCase } from "../../utils";

export default forwardRef<
    FormHandle<Nullable<DynamicLoops>> | null,
    { setErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>> }
>(function DynamicLoopsForm(
    props: { setErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>> },
    ref: React.ForwardedRef<FormHandle<Nullable<DynamicLoops>> | null>
) {
    const { formData, loading, error, setFromData } = useDataInputForm({
        formId: FORM_IDS.DYNAMIC_LOOPS,
    });

    const [formValues, setFormValues] = useState<Nullable<DynamicLoops>>();

    const [validationErrors, setValidationErrors] =
        useState<Partial<Record<keyof DynamicLoops, string>>>();

    useEffect(() => {
        if (formData) {
            setFormValues(formData as DynamicLoops);
        }
    }, [formData]);

    const { setErrors } = props;

    const { submissionLoading } = useImperativeFormSubmit<DynamicLoops>({
        formValues,
        formData,
        ref,
        onSubmitSuccess: setFromData,
        submitFunction: submitDynamicLoops,
    });

    type FormFields = DataInputGenericFormProps<DynamicLoops>["formFields"];

    const formFields = useMemo<FormFields>(
        (): FormFields =>
            !formValues || !formData
                ? []
                : [
                      {
                          id: "volumeLimitation",
                          label: "Volume limitation (%)",
                          value: formValues.volumeLimitation.measured,
                          valueToDisplay: formValues.volumeLimitation.measured,
                          originalValue: formData.volumeLimitation.measured,
                          type: InputTypes.NUMBER,
                          error: validationErrors?.volumeLimitation,
                          required: false,
                          isSmallInput: true,
                          validation: { isInteger: true },
                      },
                      {
                          id: "flowLimitation",
                          label: "Flow limitation (%)",
                          value:
                              formValues.flowLimitation.measured ?? undefined,
                          valueToDisplay:
                              formValues.flowLimitation.measured ?? undefined,
                          originalValue:
                              formData.flowLimitation.measured ?? undefined,
                          type: InputTypes.NUMBER,
                          error: validationErrors?.flowLimitation,
                          required: false,
                          isSmallInput: true,
                          validation: { isInteger: true },
                      },
                  ],
        [formValues, formData, validationErrors]
    );

    if (error) {
        return <Error />;
    }

    const validateOnFormValuesChange = async ({
        value,
        fieldSettings,
    }: {
        value: Nullable<string | number>;
        fieldSettings: FormFields[number];
    }) => {
        const valueToSave = value === 0 ? null : value;
        const snakeCaseFieldId = toSnakeCase(fieldSettings.id);

        const validationRes = await fetcher(
            `${FV_LOOPS_VALIDATION_ENDPOINT}`,
            {
                method: "POST",
                body: JSON.stringify({
                    [snakeCaseFieldId]: { measured: valueToSave },
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            },
            true
        );
        const error = validationRes?.[snakeCaseFieldId]?.error_message;
        const hasError = Boolean(!validationRes || error);

        setValidationErrors((prev) => ({
            ...prev,
            [fieldSettings.id]: hasError ? error : undefined,
        }));

        setErrors((prev) => ({
            ...prev,
            [`dynamicLoops_${fieldSettings.id}`]: hasError,
        }));

        const newValues =
            value === null || isNaN(Number(value))
                ? null // if not a number, don't update the value
                : Number(value);

        setFormValues((prevFormValues: Nullable<DynamicLoops>) => {
            if (!prevFormValues || !formData) {
                return prevFormValues;
            }
            return {
                ...prevFormValues,
                [fieldSettings.id]: {
                    ...formData[fieldSettings.id as keyof DynamicLoops],
                    measured: newValues,
                },
            };
        });
    };

    return (
        <DataInputGenericForm
            formTitle="Dynamic F/V Loops"
            formFields={formFields}
            formData={formValues}
            onFromValuesChange={validateOnFormValuesChange}
            loading={loading || submissionLoading}
        />
    );
});
